import OpenAI from 'openai';
import Anthropic from '@anthropic-ai/sdk';

// Model provider configurations
export const MODEL_PROVIDERS = {
  OPENAI: 'openai',
  ANTHROPIC: 'anthropic',
} as const;

export type ModelProvider = typeof MODEL_PROVIDERS[keyof typeof MODEL_PROVIDERS];

// Available models configuration
export const AVAILABLE_MODELS = {
  'gpt-4': {
    provider: 'openai' as ModelProvider,
    name: 'gpt-4',
    maxTokens: 8192,
    temperature: 0.7,
    description: 'Most capable GPT-4 model, better at complex tasks',
  },
  'gpt-4o-mini': {
    provider: 'openai' as ModelProvider,
    name: 'gpt-4o-mini',
    maxTokens: 4096,
    temperature: 0.7,
    description: 'Smaller, faster version of GPT-4',
  },
  'claude-3.5': {
    provider: 'anthropic' as ModelProvider,
    name: 'claude-3.5',
    maxTokens: 16384,
    temperature: 0.7,
    description: 'Anthropic\'s Claude 3.5 model',
  },
} as const;

export type ModelName = keyof typeof AVAILABLE_MODELS;
export type ModelConfig = typeof AVAILABLE_MODELS[ModelName];

// Initialize API clients with better error handling
const initOpenAI = () => {
  const apiKey = process.env.REACT_APP_OPENAI_API_KEY;
  if (!apiKey) {
    console.error('OpenAI API key is missing');
  }
  return new OpenAI({
    apiKey: apiKey || '',
    dangerouslyAllowBrowser: true,
  });
};

const initAnthropic = () => {
  const apiKey = process.env.REACT_APP_ANTHROPIC_API_KEY;
  if (!apiKey) {
    console.error('Anthropic API key is missing');
  }
  return new Anthropic({
    apiKey: apiKey || '',
    dangerouslyAllowBrowser: true
  });
};

export const openai = initOpenAI();
export const anthropic = initAnthropic();

// Helper function to get the appropriate client for a model
export function getClientForModel(modelName: ModelName): OpenAI | Anthropic {
  const modelConfig = AVAILABLE_MODELS[modelName];
  if (!modelConfig) {
    throw new Error(`Invalid model name: ${modelName}`);
  }

  if (modelConfig.provider === 'openai') {
    if (!process.env.REACT_APP_OPENAI_API_KEY) {
      throw new Error('OpenAI API key is not configured');
    }
    return openai;
  } else if (modelConfig.provider === 'anthropic') {
    if (!process.env.REACT_APP_ANTHROPIC_API_KEY) {
      throw new Error('Anthropic API key is not configured');
    }
    return anthropic;
  }

  throw new Error(`Unsupported model provider: ${modelConfig.provider}`);
}

// Type guard functions
export function isOpenAIClient(client: OpenAI | Anthropic): client is OpenAI {
  return client instanceof OpenAI;
}

export function isAnthropicClient(client: OpenAI | Anthropic): client is Anthropic {
  return client instanceof Anthropic;
}

// Default model selection
export const DEFAULT_MODEL: ModelName = 'gpt-4o-mini';
